import type React from "react";
import { forwardRef } from "react";

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle2, Globe } from "lucide-react";

interface UrlInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "type" | "onChange"
  > {
  onUrlChange?: (url: string, isValid: boolean) => void;
  showValidation?: boolean;
  validationMessage?: {
    invalid?: string;
    protocol?: string;
  };
}

const UrlInput = forwardRef<HTMLInputElement, UrlInputProps>(
  (
    {
      onUrlChange,
      showValidation = true,
      validationMessage = {},
      className,
      placeholder = "https://example.com",
      ...props
    },
    ref,
  ) => {
    const [url, setUrl] = useState("");
    const [error, setError] = useState("");
    const [isValid, setIsValid] = useState(false);
    const [touched, setTouched] = useState(false);

    const validateUrl = (value: string) => {
      if (!value) {
        setError("");
        setIsValid(false);
        onUrlChange?.(value, false);
        return;
      }

      try {
        const urlObj = new URL(value);
        // Check if it's http or https
        if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") {
          const protocolError =
            validationMessage.protocol || "URL must use http:// or https://";
          setError(protocolError);
          setIsValid(false);
          onUrlChange?.(value, false);
          return;
        }
        setError("");
        setIsValid(true);
        onUrlChange?.(value, true);
      } catch {
        const invalidError =
          validationMessage.invalid || "Please enter a valid URL";
        setError(invalidError);
        setIsValid(false);
        onUrlChange?.(value, false);
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setUrl(value);
      validateUrl(value);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setTouched(true);
      props.onBlur?.(e);
    };

    const showError = touched && error && showValidation;
    const showSuccess = touched && isValid && url && showValidation;

    return (
      <div className="space-y-2">
        <div className="relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <Globe className="h-4 w-4" />
          </div>
          <Input
            {...props}
            ref={ref}
            type="url"
            placeholder={placeholder}
            value={url}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`pl-10 pr-10 ${
              showError
                ? "border-red-500 focus-visible:ring-red-500"
                : showSuccess
                ? "border-green-500 focus-visible:ring-green-500"
                : ""
            } ${className || ""}`}
          />
          {showSuccess && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500">
              <CheckCircle2 className="h-4 w-4" />
            </div>
          )}
          {showError && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500">
              <AlertCircle className="h-4 w-4" />
            </div>
          )}
        </div>

        {showError && (
          <p className="text-sm text-red-500 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {error}
          </p>
        )}

        {showSuccess && (
          <p className="text-sm text-green-600 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Valid URL format
          </p>
        )}
      </div>
    );
  },
);

UrlInput.displayName = "UrlInput";

export default UrlInput;
